/**
 * @fileoverview Multi-seleção de filas/setores com popover, busca, "marcar todas"
 * e highlight colorido por fila. Mesmo idioma visual do QueuePickerPopover
 * (single), porém com value em array e toggle sem fechar o popover.
 */

import React, { useMemo, useState } from "react";
import {
  ButtonBase,
  Popover,
  Typography,
  IconButton,
  Divider,
  InputBase,
  makeStyles,
} from "@material-ui/core";
import { Check, Search, X, ChevronDown, ListChecks } from "lucide-react";

const getColorWithOpacity = (color, opacity, fallback) => {
  if (!color || typeof color !== "string") return fallback;
  const normalized = color.trim();
  const hexMatch = normalized.match(/^#([0-9a-f]{3}){1,2}$/i);
  if (hexMatch) {
    let hex = normalized.slice(1);
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  const rgbaMatch = normalized.match(
    /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/i
  );
  if (rgbaMatch) {
    const [, r, g, b] = rgbaMatch;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return fallback;
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    // Trigger alinhado ao estilo dos inputs do modal (formField)
    trigger: {
      minWidth: 0,
      height: 44,
      borderRadius: 14,
      padding: theme.spacing(0, 1.75),
      justifyContent: "space-between",
      transition: "background-color 0.2s ease, border-color 0.2s ease",
      border: dark ? "1px solid rgba(91, 124, 255, 0.15)" : "1px solid rgba(0, 0, 0, 0.08)",
      background: dark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      "&:hover": {
        background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(61, 92, 253, 0.04)",
        border: dark ? "1px solid rgba(91, 124, 255, 0.25)" : "1px solid rgba(61, 92, 253, 0.15)",
      },
    },
    triggerFullWidth: { width: "100%" },
    triggerContent: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.8),
      minWidth: 0,
      flex: 1,
    },
    triggerLabel: {
      display: "block",
      fontSize: "0.95rem",
      color: dark ? "#f8fafc" : "#0f172a",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    triggerIcon: { color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280", flexShrink: 0 },
    placeholder: { color: dark ? "rgba(248, 250, 252, 0.5)" : "#94a3b8" },

    popover: {
      "& .MuiPopover-paper": {
        width: 320,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
        marginTop: theme.spacing(1),
        borderRadius: 20,
        overflow: "hidden",
        background: dark ? "#22262b" : "#ffffff",
        color: dark ? "#f8fafc" : "#111827",
        border: dark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: dark
          ? "0 20px 48px rgba(0, 0, 0, 0.45)"
          : "0 20px 44px rgba(15, 23, 42, 0.16)",
      },
    },
    popoverHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(1.75, 2, 1),
    },
    popoverTitle: { fontSize: "1rem", fontWeight: 700 },
    popoverClose: { color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280" },
    popoverBody: {
      padding: theme.spacing(0, 1.5, 1.5),
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    },
    searchField: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.7, 1.4),
      borderRadius: 16,
      border: dark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(15, 23, 42, 0.12)",
      background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)",
    },
    searchIcon: { color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280" },
    searchInput: {
      flex: 1,
      color: dark ? "#f8fafc" : "#111827",
      "& input::placeholder": {
        color: dark ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
        opacity: 1,
      },
    },
    statusRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(1.25, 0.5, 1.25),
    },
    statusText: {
      fontSize: "0.9rem",
      color: dark ? "rgba(248, 250, 252, 0.76)" : "#6b7280",
    },
    clearBtn: {
      fontSize: "0.82rem",
      fontWeight: 600,
      color: dark ? "#7aa2ff" : theme.palette.primary.main,
      padding: theme.spacing(0.25, 0.75),
      borderRadius: 8,
    },
    divider: { marginBottom: theme.spacing(0.75) },
    optionsScrollArea: {
      maxHeight: 320,
      minHeight: 0,
      overflowY: "auto",
      paddingRight: theme.spacing(0.5),
      marginRight: theme.spacing(-0.5),
    },
    option: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(1.1, 1.4),
      borderRadius: 14,
      marginBottom: theme.spacing(0.75),
      textAlign: "left",
      background: "transparent",
    },
    optionSelected: {
      background: dark ? "rgba(255, 255, 255, 0.16)" : "rgba(59, 130, 246, 0.14)",
    },
    allOption: {
      border: dark ? "1px dashed rgba(255, 255, 255, 0.14)" : "1px dashed rgba(15, 23, 42, 0.14)",
    },
    optionLeft: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      minWidth: 0,
    },
    allOptionIcon: { color: dark ? "#7aa2ff" : theme.palette.primary.main, flexShrink: 0 },
    optionLabel: {
      fontSize: "0.98rem",
      color: dark ? "#f8fafc" : "#111827",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    optionCheck: {
      marginLeft: theme.spacing(1.5),
      flexShrink: 0,
      color: dark ? "#7aa2ff" : theme.palette.primary.main,
    },
    emptyState: {
      padding: theme.spacing(1.5, 0.5, 0.5),
      fontSize: "0.94rem",
      color: dark ? "rgba(248, 250, 252, 0.6)" : "#6b7280",
    },
  };
});

const QueueMultiSelectPopover = ({
  value = [],
  onChange,
  queues = [],
  placeholder = "Selecione",
  title = "Filas",
  searchPlaceholder = "Pesquisar",
  allLabel = "Todas as filas",
  emptyLabel = "Nenhum selecionado",
  selectedLabel = "selecionado(s)",
  clearLabel = "Limpar",
  noQueuesFoundLabel = "Nenhuma fila encontrada",
  disabled = false,
  fullWidth = false,
  className,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const valueSet = useMemo(() => new Set(value), [value]);
  const allSelected = queues.length > 0 && value.length >= queues.length;

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return queues;
    return queues.filter((queue) => (queue.name || "").toLowerCase().includes(q));
  }, [queues, searchTerm]);

  const toggleOne = (id) => {
    if (valueSet.has(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };
  const toggleAll = () => {
    onChange(allSelected ? [] : queues.map((q) => q.id));
  };

  const isPlaceholder = value.length === 0;
  const triggerLabel = isPlaceholder
    ? placeholder
    : allSelected
    ? allLabel
    : queues
        .filter((q) => valueSet.has(q.id))
        .map((q) => q.name)
        .join(", ");

  return (
    <>
      <ButtonBase
        className={[
          classes.trigger,
          fullWidth ? classes.triggerFullWidth : "",
          className || "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleOpen}
        disabled={disabled}
      >
        <div className={classes.triggerContent}>
          <Typography
            className={`${classes.triggerLabel} ${
              isPlaceholder ? classes.placeholder : ""
            }`}
          >
            {triggerLabel}
          </Typography>
        </div>
        <ChevronDown className={classes.triggerIcon} size={18} />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.popover}
      >
        <div className={classes.popoverHeader}>
          <Typography className={classes.popoverTitle}>{title}</Typography>
          <IconButton size="small" className={classes.popoverClose} onClick={handleClose}>
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.popoverBody}>
          <div className={classes.searchField}>
            <Search className={classes.searchIcon} size={18} />
            <InputBase
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className={classes.searchInput}
              autoFocus
            />
          </div>

          <div className={classes.statusRow}>
            <Typography className={classes.statusText}>
              {value.length > 0 ? `${value.length} ${selectedLabel}` : emptyLabel}
            </Typography>
            {value.length > 0 && (
              <ButtonBase className={classes.clearBtn} onClick={() => onChange([])}>
                {clearLabel}
              </ButtonBase>
            )}
          </div>

          <Divider className={classes.divider} />

          <div className={classes.optionsScrollArea}>
            <ButtonBase
              className={`${classes.option} ${classes.allOption} ${
                allSelected ? classes.optionSelected : ""
              }`}
              onClick={toggleAll}
            >
              <span className={classes.optionLeft}>
                <ListChecks className={classes.allOptionIcon} size={17} />
                <Typography className={classes.optionLabel}>{allLabel}</Typography>
              </span>
              {allSelected && <Check className={classes.optionCheck} size={18} />}
            </ButtonBase>

            {filtered.length === 0 ? (
              <Typography className={classes.emptyState}>{noQueuesFoundLabel}</Typography>
            ) : (
              filtered.map((queue) => {
                const isSelected = valueSet.has(queue.id);
                const background = getColorWithOpacity(
                  queue.color,
                  isSelected ? 0.26 : 0.16,
                  isSelected ? "rgba(59, 130, 246, 0.18)" : "rgba(59, 130, 246, 0.08)"
                );
                return (
                  <ButtonBase
                    key={queue.id}
                    className={`${classes.option} ${isSelected ? classes.optionSelected : ""}`}
                    onClick={() => toggleOne(queue.id)}
                    style={{ backgroundColor: background }}
                  >
                    <span className={classes.optionLeft}>
                      <Typography className={classes.optionLabel}>{queue.name}</Typography>
                    </span>
                    {isSelected && <Check className={classes.optionCheck} size={18} />}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default QueueMultiSelectPopover;
